// src/hooks/programs/useProgramDraft.js
import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Safely extract current user ID from localStorage
 */
const getCurrentUserId = () => {
    try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            const user = JSON.parse(userStr);
            return user?.id || "default";
        }
    } catch (e) {
        // ignore parse error
    }
    return "default";
};

/**
 * Sanitize form data before storing in localStorage:
 * - Omits non-serializable File objects
 * - Truncates excessively large base64 image strings to prevent quota exhaustion
 */
const sanitizeFormData = (data) => {
    if (!data) return null;
    const sanitized = { ...data };

    // Don't store raw File objects
    if (sanitized.cover_image && typeof sanitized.cover_image === "object" && (sanitized.cover_image instanceof File || sanitized.cover_image instanceof Blob)) {
        sanitized.cover_image = null;
        sanitized._hadCoverFile = true;
    }

    // Don't store massive base64 strings in localStorage (> 200KB)
    if (
        typeof sanitized.cover_image_preview === "string" &&
        sanitized.cover_image_preview.startsWith("data:") &&
        sanitized.cover_image_preview.length > 200000
    ) {
        sanitized.cover_image_preview = "";
        sanitized._hadCoverPreview = true;
    }

    // Reset temporary UI upload flags
    delete sanitized.uploadingCoverImage;
    delete sanitized.coverImageError;
    delete sanitized.coverImageSuccess;

    return sanitized;
};

/**
 * Hook to manage auto-saving and restoring program drafts
 */
const useProgramDraft = ({ isEdit = false, programId = null }) => {
    const userId = getCurrentUserId();
    const storageKey = isEdit && programId
        ? `program_form_draft_edit_${programId}_${userId}`
        : `program_form_draft_create_${userId}`;

    const [hasDraft, setHasDraft] = useState(false);
    const [draftTimestamp, setDraftTimestamp] = useState(null);
    const [draftStep, setDraftStep] = useState(null);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const debounceTimerRef = useRef(null);
    const latestDataRef = useRef({ formData: null, step: 0 });

    // Check if a saved draft exists on mount
    const checkDraft = useCallback(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.formData && typeof parsed.currentStep === "number") {
                    setHasDraft(true);
                    setDraftTimestamp(parsed.updatedAt || null);
                    setDraftStep(parsed.currentStep);
                    return parsed;
                }
            }
        } catch (e) {
            console.warn("Failed to check draft from localStorage", e);
        }
        setHasDraft(false);
        setDraftTimestamp(null);
        setDraftStep(null);
        return null;
    }, [storageKey]);

    // Initial check
    useEffect(() => {
        checkDraft();
    }, [checkDraft]);

    // Save draft immediately to localStorage
    const saveDraftNow = useCallback((formData, currentStep) => {
        if (!formData) return;
        try {
            const sanitized = sanitizeFormData(formData);
            const now = Date.now();
            const payload = {
                version: 1,
                updatedAt: now,
                currentStep: currentStep ?? 0,
                formData: sanitized,
            };
            localStorage.setItem(storageKey, JSON.stringify(payload));
            setHasDraft(true);
            setDraftTimestamp(now);
            setDraftStep(currentStep ?? 0);
            setLastSavedAt(now);
        } catch (e) {
            console.warn("Could not save program draft (quota exceeded or storage disabled)", e);
        }
    }, [storageKey]);

    // Debounced save
    const saveDraft = useCallback((formData, currentStep) => {
        latestDataRef.current = { formData, step: currentStep };
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            saveDraftNow(latestDataRef.current.formData, latestDataRef.current.step);
        }, 600);
    }, [saveDraftNow]);

    // Clear draft from localStorage
    const clearDraft = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        try {
            localStorage.removeItem(storageKey);
        } catch (e) {
            // ignore
        }
        setHasDraft(false);
        setDraftTimestamp(null);
        setDraftStep(null);
        setLastSavedAt(null);
    }, [storageKey]);

    // Save before page unload / refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (latestDataRef.current?.formData) {
                saveDraftNow(latestDataRef.current.formData, latestDataRef.current.step);
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [saveDraftNow]);

    // Load draft data
    const loadDraft = useCallback(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.formData) {
                    return parsed;
                }
            }
        } catch (e) {
            console.warn("Failed to load draft from localStorage", e);
        }
        return null;
    }, [storageKey]);

    return {
        hasDraft,
        draftTimestamp,
        draftStep,
        lastSavedAt,
        saveDraft,
        saveDraftNow,
        loadDraft,
        clearDraft,
        storageKey,
    };
};

export default useProgramDraft;

